import { motion } from "framer-motion";
import {
    DoorOpen,
    Users,
    MessageSquareText,
    Megaphone,
    HandCoins,
    CalendarRange,
    Newspaper,
    FileSignature,
    BarChart3,
    Globe,
    Phone,
    Target,
} from "lucide-react";

const SERVICES = [
    {
        icon: Target,
        title: "Field Operations & GOTV",
        desc: "Build a turnout machine — universe targeting, walk lists, daily field discipline, and Election-Day execution.",
    },
    {
        icon: DoorOpen,
        title: "Door-to-Door Canvassing",
        desc: "Persuasion and ID programs run the way they should be — at the door, by trained canvassers with real conversation goals.",
    },
    {
        icon: Users,
        title: "Volunteer Recruitment & Training",
        desc: "Find them, train them, keep them. We build volunteer teams that show up and produce week after week.",
    },
    {
        icon: Phone,
        title: "Texting & Phone Banking",
        desc: "Peer-to-peer texting and disciplined phone banks — every contact tracked, every script tuned to your race.",
    },
    {
        icon: MessageSquareText,
        title: "Campaign Messaging",
        desc: "Messaging that lands at the door, in mailboxes, on doorsteps, and on screens — built for your district, not a template.",
    },
    {
        icon: HandCoins,
        title: "Fundraising Strategy",
        desc: "Call-time discipline, finance committee building, event ladders, and donor stewardship that actually closes.",
    },
    {
        icon: CalendarRange,
        title: "Event Planning & Coordination",
        desc: "Launches, town halls, meet-and-greets, and rallies — designed to move voters, donors, and the press.",
    },
    {
        icon: Newspaper,
        title: "Press & Media Outreach",
        desc: "Press releases, surrogate prep, earned media plans, and rapid response when the news cycle moves.",
    },
    {
        icon: FileSignature,
        title: "Petition Programs",
        desc: "Ballot access, signature drives, and validation workflows — managed cleanly so your race never stalls on paper.",
    },
    {
        icon: BarChart3,
        title: "Data Analysis & Voter Targeting",
        desc: "Modeled scores, turnout universes, and post-contact analytics — so every door, dollar, and dial is aimed.",
    },
    {
        icon: Globe,
        title: "Campaign Websites & Digital",
        desc: "Fast, professional campaign sites and digital presence that convert visitors into volunteers and donors.",
    },
    {
        icon: Megaphone,
        title: "Grassroots Organization",
        desc: "County leadership, coalition building, and ward-level structures that outlast any single race.",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-28 md:py-36 bg-ink overflow-hidden"
        >
            <div className="absolute top-0 inset-x-0 h-px hairline-gold" />

            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="h-px w-8 bg-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold">
                                02 / What We Do
                            </span>
                        </div>
                        <h2
                            data-testid="services-headline"
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl"
                        >
                            Full-service{" "}
                            <span className="italic text-gold/90">campaign support.</span>
                            <br />
                            Built for the field.
                        </h2>
                    </div>
                    <p className="text-cream/65 max-w-md text-base md:text-lg leading-relaxed">
                        From the first volunteer meeting to your Election-Night party,
                        we run the disciplined, hands-on operation a winning campaign
                        actually requires.
                    </p>
                </div>

                <div
                    data-testid="services-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/15 border border-gold/15"
                >
                    {SERVICES.map((s, idx) => {
                        const Icon = s.icon;
                        return (
                            <motion.article
                                key={s.title}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: (idx % 6) * 0.05,
                                }}
                                data-testid={`service-card-${idx}`}
                                className="group relative bg-[#070707] p-8 md:p-10 h-full hover:bg-[#0c0c0c] transition-colors"
                            >
                                <div className="flex items-center justify-between mb-8">
                                    <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-ink transition-colors">
                                        <Icon size={20} strokeWidth={1.6} />
                                    </div>
                                    <span className="font-mono text-[10px] text-cream/30 tracking-widest">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl text-cream mb-3 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-cream/65 text-sm md:text-base leading-relaxed">
                                    {s.desc}
                                </p>
                                <div className="mt-8 h-px w-10 bg-gold/40 group-hover:w-20 transition-all duration-300" />
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
